import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { MonoLabel } from '@/components/editorial';

export interface WizardStep {
  id: string;
  label: string;
}

interface Props {
  steps: WizardStep[];
  currentIndex: number;
  /** The save state ("Saving…" / "Saved just now"). Rendered once. */
  status?: ReactNode;
  /** A `(?)` marker about which step the family lands on (#2440), set by the wizard. */
  stepMarker?: ReactNode;
  /**
   * #2527: open a step from the list. With it, each step the wizard allows
   * (`canSelect`) is a button; without it the list is read-only, as before.
   */
  onSelect?: (index: number) => void;
  /** Which steps `onSelect` may open. Defaults to none. */
  canSelect?: (index: number) => boolean;
  /** A `(?)` marker about which steps the list lets a family open. */
  listMarker?: ReactNode;
  /** #2752: the /renew walk. The progress bar is named "Renewal progress"
   *  once the reader's language has that line; until then it keeps the
   *  language's own application wording rather than switching to English. */
  renewal?: boolean;
}

/**
 * #2448: one block, two placements, and the save state renders exactly once.
 * On a phone it is the strip above the step: the segment bar, then
 * "Step 2 of 8 — label" and the step menu, then the save state on a line of
 * its own. #2940/#2941: that line is always there at a fixed height, filled
 * or empty, so a long save label ("Guardado ✓ · hace un momento") never
 * squeezes the title or moves the form below it mid-click. From `lg` up the wizard puts this block
 * in the column beside the form, where it becomes a step list (what is done,
 * where the family is, what is left) with the save state under it.
 */
export function WizardProgress({ steps, currentIndex, status, stepMarker, onSelect, canSelect, listMarker, renewal = false }: Props) {
  const { t, i18n } = useTranslation('forms');
  /** #2752: the renewal label when this is a renewal and the language has it. */
  const renewalLine = (): boolean => {
    if (!renewal) return false;
    const lng = i18n.resolvedLanguage ?? i18n.language ?? 'en';
    if (lng === 'en' || lng.startsWith('en-')) return true;
    const value: unknown = i18n.getResource(lng, 'forms', 'wizard.progress.labelRenewal');
    return typeof value === 'string' && value.trim() !== '';
  };
  // #2544: below `lg` the list beside the form is hidden, so the phone strip
  // gets a compact step menu that opens the same list.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  // The menu belongs to the step it was opened on: a jump (from here or the
  // more-info banner) or Continue closes it, and so does Escape.
  useEffect(() => { setMenuOpen(false); }, [currentIndex]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);
  /** The step list. `onPick` runs after a step is chosen (the phone menu closes). */
  const stepList = (onPick?: () => void) => (
    <ol className="space-y-1.5">
      {steps.map((step, i) => {
        const tone = i === currentIndex ? 'font-medium text-foreground' : i < currentIndex ? 'text-stevens-ink-2' : 'text-stevens-ink-3';
        const dot = (
          <span
            aria-hidden="true"
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
              i < currentIndex ? 'bg-stevens-indigo' : i === currentIndex ? 'bg-stevens-indigo/60' : 'bg-border'
            }`}
          />
        );
        const selectable = Boolean(onSelect) && i !== currentIndex && (canSelect?.(i) ?? false);
        return (
          <li key={step.id} className={`text-body-sm ${tone}`}>
            {selectable ? (
              <button
                type="button"
                onClick={() => { onSelect?.(i); onPick?.(); }}
                className="flex w-full items-center gap-2 rounded-sm text-start hover:text-stevens-indigo hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {dot}
                <span>{step.label}</span>
              </button>
            ) : (
              <span className="flex items-center gap-2" aria-current={i === currentIndex ? 'step' : undefined}>
                {dot}
                <span>{step.label}</span>
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
  return (
    <div className="w-full space-y-2">
      {/* Segment bar — the phone's compact progress. */}
      <div className="flex gap-1 lg:hidden" role="progressbar" aria-valuenow={currentIndex + 1} aria-valuemax={steps.length} aria-label={renewalLine() ? t('wizard.progress.labelRenewal') : t('wizard.progress.label')}>
        {steps.map((step, i) => (
          <div
            key={step.id}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i < currentIndex
                ? 'bg-stevens-indigo'
                : i === currentIndex
                ? 'bg-stevens-indigo/60'
                : 'bg-border'
            }`}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 lg:flex-col lg:flex-nowrap lg:items-stretch lg:gap-y-3">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <MonoLabel className="block min-w-0">
          {t('wizard.progress.step', { current: currentIndex + 1, total: steps.length })}
          <span className="lg:hidden">
            {' — '}
            {/* #2487: no `capitalize` on a step name, here or in the list
                below. Each locale's string carries its own casing; CSS
                title-casing broke French and Spanish. */}
            <span className="font-serif text-foreground">
              {steps[currentIndex]?.label}
            </span>
          </span>
        </MonoLabel>
        {stepMarker}
        {onSelect && (
          <button
            type="button"
            data-testid="wizard-step-menu-toggle"
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? menuId : undefined}
            onClick={() => setMenuOpen((open) => !open)}
            className="ms-1 inline-flex shrink-0 items-center gap-0.5 rounded-sm text-body-sm text-stevens-indigo hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
          >
            {t('wizard.progress.menuToggle')}
            <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
        </div>
        {/* The step list — wide screens only, beside the form. #2527: each
            step the wizard allows is a button, so fixing a step-3 answer from
            the end is one click, not ten presses of Back. The current step
            carries aria-current; a step the family cannot open yet is text. */}
        <nav className="hidden lg:block" aria-label={t('wizard.progress.listLabel')}>
          {stepList()}
          {listMarker}
        </nav>
        {/* #2940: below `lg` the save state takes its own fixed-height line. */}
        <div data-testid="wizard-save-status" className="h-[18px] basis-full overflow-hidden lg:h-auto lg:min-h-[18px] lg:basis-auto">{status}</div>
      </div>
      {/* #2544: the phone step menu — the same list, opened from the strip. */}
      {onSelect && menuOpen && (
        <nav aria-label={t('wizard.progress.listLabel')}
          id={menuId}
          data-testid="wizard-step-menu"
          className="rounded-md border border-border bg-card p-3 lg:hidden"
        >
          {stepList(() => setMenuOpen(false))}
        </nav>
      )}
    </div>
  );
}
